import { useCallback, useEffect, useMemo, useState } from "react";
import { Dialog } from "primereact/dialog";
import storage from "../../../utils/storage";
import { useDispatch, useSelector } from "react-redux";
import appActions from "../../../appActions";
import PrimeTable from "../../../components/PrimeTable";
import SitesForm from "./SitesForm";
import SitesFilterBar from "./SitesFilterBar";
import SitesHeader from "./SitesHeader";
import Sidebar from "../../../components/Sidebar";

const SitesList = () => {

    const dispatch = useDispatch();

    const userInfo = useMemo(() => JSON.parse(storage.get('loginUser')), []);
  //  const { userRole, userId, siteId } = userInfo;

    const { sitesList, siteDetails } = useSelector((state) => state?.sites);
    const siteGroupOptions = useSelector((state) => state.common.siteGroupList);

    const defaultParams = useMemo(() => ({
        pageno: 1,
        pagesize: 10,
        sort: 'createdOn',
        sortOrder : -1,
    }), []);

    const [formValues, setFormValues] = useState(defaultParams);
    const [filterParams, setFilterParams] = useState({});

    // New states for dialog control and selected user
    const [isDialogVisible, setIsDialogVisible] = useState(false);
    const [selectedSite, setSelectedSite] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [isViewMode, setIsViewMode] = useState(false);
    const [siteRecords, setSiteRecords] = useState([]);

    const fetchSites = useCallback((params = {}) => {
        // dispatch(appActions.SITEGROUPLIST_GET_ALL_REQUEST({ ...params }));
        dispatch(appActions.SITES_GET_ALL_REQUEST({ ...defaultParams, ...params }));
        dispatch(appActions.STORE_GET_ALL_REQUEST({ ...params }));
    }, [dispatch, defaultParams]);

    const handleFilterTask = useCallback((params) => {
        setFilterParams(params);
        fetchSites(params);
    }, [fetchSites]);

    useEffect(() => {
        fetchSites();
    }, [dispatch]);

    useEffect(() => {
        if (sitesList && siteGroupOptions) {
            const updatedSitesData = sitesList?.data?.map(site => {
                const siteGroups = site?.siteGroup?.split(",");;
                // Initialize an array to hold descriptions
                const descriptions = [];
                
                // Check for matches in siteGroupOptions
                siteGroups?.forEach(sitegroup => {
                    const match = siteGroupOptions?.find(option => option.siteCode === sitegroup.trim());
                    if (match) {
                        descriptions.push(match.siteName);
                    }
                    else {
                        descriptions.push(sitegroup);
                    }
                });

                // Join the descriptions into a single string or keep as an array if needed
                const siteGroupDesc = descriptions?.join(", ") || "";

                // Return the updated task with the new description
                return {
                    ...site,
                    siteGroupDesc
                };
            })

            setSiteRecords({...sitesList, data: updatedSitesData});
        }
    }, [sitesList, siteGroupOptions]);

    const handleViewRedirect = (record) => {
        const data = siteRecords?.data?.find((t) => t.id === record.id);
        setSelectedSite(data);
        setIsViewMode(true);
        setIsEditMode(false);
        setIsDialogVisible(true); // Open dialog in view mode
    };

    const handleEditRedirect = (record) => {
        const data = siteRecords?.data?.find((t) => t.id === record.id);
        setSelectedSite(data);
        setIsEditMode(true);
        setIsViewMode(false);
        setIsDialogVisible(true); // Open dialog in edit mode
    };

    const handleDelete = useCallback((record) => {
        const { id } = record;
        dispatch(appActions.SITES_DELETE_REQUEST({ id }));
    }, [dispatch]);

    const handleDialogClose = () => {
        setIsDialogVisible(false);
        setSelectedSite(null);
    };

    const handleDialogSave = () => {
        fetchSites();
        setIsDialogVisible(false);
    }

    const handleSearch = useCallback(
        (params) => {

            const searchParams = {
                ...params,
                pagesize: params.pagesize || defaultParams?.pagesize,
                pageno: params.pageno || defaultParams?.pageno,
                sort: params.sort || defaultParams?.sort,
                sortOrder: params.sortOrder || defaultParams?.sortOrder,
            };

            dispatch(appActions.SITES_GET_ALL_REQUEST(searchParams));
        },
        [dispatch],
    );

    const onFilterChangeCallback  = useCallback(
        (event) => {

            const { sortField, sortOrder, rows, page } = event;
            
            const modifiedSortField = sortField === 'siteGroupDesc' ? 'siteGroup' : sortField
            const params = {...formValues, pagesize: rows, pageno: page, sort: modifiedSortField, sortOrder: sortOrder };

            setFormValues(prevFormValues => ({
                ...prevFormValues,
                sort: sortField, 
                sortOrder: sortOrder,
                pagesize: rows,
                pageno: page
            }));

            handleSearch(params); 
        },

        [handleSearch, formValues],
    );
    
    useEffect(() => {
        if (siteDetails?.action === 'delete' || siteDetails?.action === 'create' || siteDetails?.action === 'update') {
            fetchSites();
        }
    }, [siteDetails, fetchSites]);

    const columns = [
        { field: 'siteId', header: 'Site Id', sortable: true},
        // { field: 'siteType', header: 'Site Type', sortable: true},
        { field: 'siteGroupDesc', header: 'Site Group', sortable: true},
        { field: 'title', header: 'Title', sortable: true},
        // { field: 'description', header: 'description', sortable: true},
        { field: 'region', header: 'Region', sortable: true},
        { field: 'country', header: 'Country', sortable: true},
        { field: 'city', header: 'City', sortable: true},
        { field: 'siteLocation', header: 'Site Location', sortable: true},
        { field: 'status', header: 'Status', sortable: true},
    ];

    const actionTemplate = useCallback(
        (rowData) => (
            <div className="flex gap-10 justify-content-center">
                <span className="cursor-pointer tp" onClick={() => handleViewRedirect(rowData)} data-pr-tooltip="View">
                    <i className="pi pi-eye text-blue-500 text-base"></i>
                </span>
                <span className="cursor-pointer tp" onClick={() => handleEditRedirect(rowData)} data-pr-tooltip="Edit">
                    <i className="pi pi-pencil text-blue-500 text-base"></i>
                </span>
                <span className="cursor-pointer tp" onClick={() => handleDelete(rowData)} data-pr-tooltip="Delete">
                    <i className="pi pi-times-circle text-base text-danger"/>
                </span>
            </div>
        ),
        [handleViewRedirect, handleEditRedirect, handleDelete],
    );

    return (
        <div className="w-full">
           
            <SitesHeader />
            <div className="flex flex-min-height">
                <Sidebar />
                <div className="p-grid w-full pl-60px">
                    <h3 className="title">Site Management</h3>
                    <SitesFilterBar
                        handleFilter={handleFilterTask}
                        filterParams={filterParams}
                        userInfo={userInfo}
                    />
                    <PrimeTable
                        list={siteRecords}
                        columns={columns}
                        actionColumn={actionTemplate}
                        tableName='site_management'
                        isLazy={true}
                        onFilterChangeCallback={(e) => {
                            onFilterChangeCallback(e);
                        }}
                        isExport={true}
                    />
                </div>

                {/* Dialog for User Form */}
                <Dialog
                    visible={isDialogVisible}
                    header={isEditMode ? "Edit Site" : "View Site"}
                    onHide={handleDialogClose}
                    style={{ width: '50vw' }}
                >
                    <SitesForm
                        site={selectedSite}
                        isEditMode={isEditMode}
                        isViewMode={isViewMode}
                        onClose={handleDialogClose}
                        onSave={handleDialogSave}
                    />
                </Dialog>
            </div>
        </div>
    );
}

export default SitesList;
